import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Category, CreateConcertRequest, MediaLink, Language, ConcertGenre } from '@/types/model';
import {
  concertGenreOptions
} from '@/constants';
import { useTranslation, useCategoriesForDropdown } from '@/hooks';
import { Translation } from '@/locales';
import Input from '@/components/ui/Input';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import MultiSelect from '@/components/ui/MultiSelect';
import LinkManager from '@/components/ui/LinkManager';
import { qualityTypes } from '@/constants';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import { MediaSource } from '@/types/media';

const createConcertSchema = (t: Translation) => z.object({
  title: z
    .string()
    .min(2, t.validation.minLength(2))
    .max(200, t.validation.maxLength(200)),
  year: z
    .number({ message: t.validation.required })
    .min(1900, 'El año debe ser mayor a 1900')
    .max(new Date().getFullYear() + 5, 'El año no puede ser mayor al año actual + 5'),
  durationMinutes: z
    .number({ message: t.validation.required })
    .min(1, 'La duración debe ser mayor a 1 minuto'),
  actors: z
    .string()
    .min(2, t.validation.minLength(2)),
  spanishSynopsis: z.string().optional(),
  englishSynopsis: z.string().optional(),
  genres: z.array(z.string()).optional(),
  categoryIds: z.array(z.string()).optional(),
  coverImageUrl: z.url().optional().or(z.literal('')),
  backgroundImageUrl: z.url().optional().or(z.literal('')),
  reference: z.string().optional(),
  active: z.boolean(),
  adultContent: z.boolean(),
  monoAudio: z.boolean(),
  englishOnly: z.boolean(),
  kidsContent: z.boolean(),
  links: z.array(
    z.object({
      id: z.string().optional(),
      quality: z.enum(qualityTypes),
      url: z.url(t.validation.urlFormat),
      audioLanguages: z.array(z.string()).optional(),
      subtitleLanguages: z.array(z.string()).optional(),
      active: z.boolean(),
    }), t.links.required
  ).min(1, t.links.required)
});

type ConcertFormData = z.infer<ReturnType<typeof createConcertSchema>>;

interface ConcertFormProps {
  onSuccess?: (data: CreateConcertRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateConcertRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const ConcertForm = ({
  onSuccess,
  onCancel,
  initialData,
  isEdit = false,
  isSubmitting = false
}: ConcertFormProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedGenres, setSelectedGenres] = useState<string[]>(initialData?.genres || []);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialData?.categoryIds || []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<ConcertFormData>({
    resolver: zodResolver(createConcertSchema(t)),
    defaultValues: {
      title: initialData?.title || '',
      year: initialData?.year || new Date().getFullYear(),
      durationMinutes: initialData?.durationMinutes || 0,
      actors: initialData?.actors || '',
      spanishSynopsis: initialData?.spanishSynopsis || '',
      englishSynopsis: initialData?.englishSynopsis || '',
      genres: initialData?.genres || [],
      categoryIds: initialData?.categoryIds || [],
      coverImageUrl: initialData?.coverImageUrl || '',
      backgroundImageUrl: initialData?.backgroundImageUrl || '',
      reference: initialData?.reference || '',
      active: initialData?.active ?? true,
      adultContent: initialData?.adultContent ?? false,
      monoAudio: initialData?.monoAudio ?? false,
      englishOnly: initialData?.englishOnly ?? false,
      kidsContent: initialData?.kidsContent ?? false,
      links: initialData?.links || []
    }
  });

  const onSubmit = async (data: ConcertFormData) => {
    setSubmitError(null);

    try {
      const formattedData: CreateConcertRequest = {
        ...data,
        genres: data.genres as ConcertGenre[],
        links: data.links?.map(link => ({
          ...link,
          id: link.id || crypto.randomUUID(),
          audioLanguages: link.audioLanguages as Language[],
          subtitleLanguages: link.subtitleLanguages as Language[],
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString()
        }))
      };
      await onSuccess?.(formattedData);
      if (!isEdit) {
        reset();
        setSelectedGenres([]);
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const useCategoriesForMusic = () => useCategoriesForDropdown('MUSIC');
  const { data: categoriesData } = useCategoriesForMusic();

  const mapCategoriesToOptions = (data: unknown[]) =>
    (data as Category[]).map(cat => ({ value: cat.id, label: cat.description }));

  const activeValue = watch('active');
  const adultContentValue = watch('adultContent');
  const monoAudioValue = watch('monoAudio');
  const englishOnlyValue = watch('englishOnly');
  const kidsContentValue = watch('kidsContent');
  const coverImageValue = watch('coverImageUrl');
  const watchedLinks = watch('links') || [];

  const handleAddLink = async (link: Omit<MediaLink, 'id' | 'createdDate' | 'updatedDate'>) => {
    try {
      const newLink = {
        ...link,
        id: crypto.randomUUID(),
        audioLanguages: link.audioLanguages?.map(a => a.toUpperCase()) || [],
        subtitleLanguages: link.subtitleLanguages?.map(s => s.toUpperCase()) || [],
      };
      setValue('links', [...watchedLinks, newLink]);
    } catch (error) {
      console.error('Error adding link:', error);
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    try {
      setValue('links', watchedLinks.filter(link => link.id !== linkId));
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  const handleUpdateLink = async (linkId: string, updatedLink: Omit<MediaLink, 'id' | 'createdDate' | 'updatedDate'>) => {
    try {
      const updatedLinks = watchedLinks.map(link =>
        link.id === linkId
          ? {
              ...link,
              ...updatedLink,
              audioLanguages: updatedLink.audioLanguages?.map(a => a.toUpperCase()) || [],
              subtitleLanguages: updatedLink.subtitleLanguages?.map(s => s.toUpperCase()) || [],
            }
          : link
      );
      setValue('links', updatedLinks);
    } catch (error) {
      console.error('Error updating link:', error);
    }
  };

  const handleToggleLink = async (linkId: string, active: boolean) => {
    try {
      const updatedLinks = watchedLinks.map(link =>
        link.id === linkId ? { ...link, active } : link
      );
      setValue('links', updatedLinks);
    } catch (error) {
      console.error('Error toggling link:', error);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEdit ? 'Editar Concierto' : 'Nuevo Concierto'}
        </h2>
        <p className="text-gray-600 mt-1">
          {isEdit ? 'Actualice la información del concierto' : 'Complete todos los campos requeridos'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Top Section with Cover Image */}
        <div className="flex gap-6">
          {/* Left Side - Form Fields */}
          <div className="flex-1 space-y-4">
            <Input
              label="Título *"
              placeholder="Título del concierto"
              error={errors.title?.message}
              {...register('title')}
            />

            <ImageUrlUploadField
              label="Imagen Cover"
              placeholder="https://example.com/cover.jpg"
              value={coverImageValue || ''}
              onChange={(url) => setValue('coverImageUrl', url, { shouldValidate: true, shouldDirty: true })}
              error={errors.coverImageUrl?.message}
              disabled={isSubmitting}
              source={MediaSource.CONCERT_POSTER}
              showPreview={false}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Año *"
                type="number"
                placeholder="2024"
                error={errors.year?.message}
                {...register('year', { valueAsNumber: true })}
              />

              <Input
                label="Duración (Minutos) *"
                type="number"
                placeholder="120"
                error={errors.durationMinutes?.message}
                {...register('durationMinutes', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div className="flex-shrink-0">
            {coverImageValue ? (
              <img
                src={coverImageValue}
                alt="Vista previa de la portada"
                className="h-58 w-40 object-cover rounded-lg shadow-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
                onLoad={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'block';
                }}
              />
            ) : (
              <div
                className="h-58 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <div className="text-xs">Vista previa</div>
                  <div className="text-xs">de portada</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Actores *
          </label>
          <textarea
            placeholder="Artistas, músicos, banda..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            {...register('actors')}
          />
          {errors.actors && (
            <p className="mt-1 text-sm text-red-600">{errors.actors.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sinopsis en Español
            </label>
            <textarea
              placeholder="Sinopsis en español..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('spanishSynopsis')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sinopsis en Inglés
            </label>
            <textarea
              placeholder="Synopsis in English..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('englishSynopsis')}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <MultiSelect
            label={"Categorías"}
            placeholder={"Seleccionar categorías"}
            options={mapCategoriesToOptions(categoriesData || [])}
            value={selectedCategories}
            onChange={(newCategories) => {
              setSelectedCategories(newCategories);
              setValue('categoryIds', newCategories);
            }}
            error={errors.categoryIds?.message}
          />
        </div>

        <MultiSelect
          label="Géneros"
          placeholder="Selecciona los géneros del concierto..."
          options={concertGenreOptions}
          value={selectedGenres}
          onChange={(newGenres) => {
            setSelectedGenres(newGenres);
            setValue('genres', newGenres);
          }}
          disabled={isSubmitting}
        />

        {/* Media URL */}
        <div className="grid grid-cols-1 gap-6">
          <ImageUrlUploadField
            label="Imagen Fondo"
            placeholder="https://example.com/background.jpg"
            value={watch('backgroundImageUrl') || ''}
            onChange={(url) => setValue('backgroundImageUrl', url, { shouldValidate: true, shouldDirty: true })}
            error={errors.backgroundImageUrl?.message}
            disabled={isSubmitting}
            source={MediaSource.CONCERT_BACKGROUND}
          />
        </div>

        <Input
          label="Referencia"
          placeholder="Referencia adicional..."
          error={errors.reference?.message}
          {...register('reference')}
        />

        {/* Toggles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          <Toggle
            label="Estado"
            checked={activeValue}
            onChange={(checked) => setValue('active', checked)}
            disabled={isSubmitting}
          />

          <Toggle
            label="Control de adulto"
            checked={adultContentValue}
            onChange={(checked) => setValue('adultContent', checked)}
            disabled={isSubmitting}
          />

          <Toggle
            label="Mono audio"
            checked={monoAudioValue}
            onChange={(checked) => setValue('monoAudio', checked)}
            disabled={isSubmitting}
          />

          <Toggle
            label="Solo Inglés"
            checked={englishOnlyValue}
            onChange={(checked) => setValue('englishOnly', checked)}
            disabled={isSubmitting}
          />

          <Toggle
            label="Para niño"
            checked={kidsContentValue}
            onChange={(checked) => setValue('kidsContent', checked)}
            disabled={isSubmitting}
          />
        </div>

        <LinkManager
          links={watchedLinks}
          onAddLink={handleAddLink}
          onUpdateLink={handleUpdateLink}
          onDeleteLink={handleDeleteLink}
          onToggleLink={handleToggleLink}
          error={errors.links?.message}
        />

        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Guardando...'
              : isEdit
                ? 'Actualizar Concierto'
                : 'Registrar'
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ConcertForm;
